import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, ChevronDown, AlertTriangle, CheckCircle2, Clock, Settings2, X, CalendarClock, Gavel } from 'lucide-react';
import { getMultasMes, MultasMes, AlumnaMulta, MotivoMulta, cancelDebt, moneyEntero as money } from '../api';
import { getSetting, putSetting } from '../../../services/settings';
import { useToast } from '../../../context/ToastContext';
import { useAuth } from '../../../context/AuthContext';
import { Skeleton } from '../../../components/Skeleton';
import EmptyState from '../../../components/EmptyState';

// MULTAS MES A MES (23-sep-2026, pedido de Carlos: «no se comprende bien quién no recibe multa»).
//
// Una sola pregunta por pantalla: en ESTE mes, ¿a quién se multó y, a quien no, por qué? Cada
// mensualidad cae en UN motivo, calculado en el backend con la misma lógica que el cron
// (lib/letsmoveLateFeeReport.js) — lo que se ve aquí es lo que el proceso hará.
//
// Aquí viven las decisiones de UN mes (prórroga, exentar a una alumna, perdonar). Las reglas que
// valen siempre (día de vencimiento, importe, tolerancia, transferencias, desfasados) siguen en
// Mi Estudio → Cobranza.

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const mesTxt = (ym: string) => `${MESES[Number(ym.slice(5, 7)) - 1] || ym} ${ym.slice(0, 4)}`;
const diaTxt = (f: string | null) => f ? `${Number(f.slice(8, 10))} de ${MESES[Number(f.slice(5, 7)) - 1]}` : '—';
const mesMas = (ym: string, n: number) => { const d = new Date(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)) - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const mesDeHoy = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const METODO: Record<string, string> = { cash: 'efectivo', wire: 'transferencia', creditcard: 'tarjeta', other: 'otro' };

// Motivos de catálogo para perdonar/exentar (§10-C del análisis del 19-sep): un clic en vez de teclear.
const MOTIVOS_RAPIDOS = ['Transferencia a tiempo (comprobante)', 'Incapacidad', 'Acuerdo con Karla', 'Primera clase del mes', 'Error de captura', 'Cortesía', 'Se dio de baja'];

// Qué se ve en cada bloque, en orden.
// 25-sep-2026: 'multa_transferencia_tarde' = pagó por transferencia FUERA de la ventana → decide Karla (Perdonar / Mantener).
const CON_MULTA: MotivoMulta[] = ['multa_transferencia_tarde', 'multa_pendiente', 'multa_cobrada', 'multa_perdonada'];
const SIN_MULTA: MotivoMulta[] = ['a_tiempo', 'prorroga', 'tolerancia', 'transferencia', 'desfasado', 'exenta', 'no_vence',
  'tarde_sin_multa', 'saldado_sin_pago', 'sin_clase', 'nunca_vino', 'mes_cerrado', 'fecha_incoherente', 'cancelada', 'baja'];
// Los que merecen una mirada (se abren solos si tienen gente).
const REVISAR: MotivoMulta[] = ['tarde_sin_multa', 'fecha_incoherente', 'sin_clase', 'mes_cerrado'];

type Accion = { tipo: 'exentar' | 'perdonar'; a: AlumnaMulta } | null;

export default function MultasPanel() {
  const toast = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [mes, setMes] = useState(mesDeHoy());
  const [data, setData] = useState<MultasMes | null>(null);
  const [loading, setLoading] = useState(true);
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());
  const [accion, setAccion] = useState<Accion>(null);
  const [fechaProrroga, setFechaProrroga] = useState('');
  const [editProrroga, setEditProrroga] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(() => {
    setLoading(true);
    getMultasMes(mes).then((r) => {
      setData(r);
      // Abrir de entrada lo que pide atención; lo normal («pagó a tiempo») queda plegado.
      setAbiertos(new Set(['por_multar', 'multa_transferencia_tarde', 'multa_pendiente', ...REVISAR.filter((k) => r.resumen[k] > 0)]));
    }).catch(() => setData(null)).finally(() => setLoading(false));
  }, [mes]);
  useEffect(() => { cargar(); }, [cargar]);

  const porMotivo = useMemo(() => {
    const m = new Map<MotivoMulta, AlumnaMulta[]>();
    for (const a of data?.alumnas || []) m.set(a.motivo, [...(m.get(a.motivo) || []), a]);
    return m;
  }, [data]);
  const n = (k: MotivoMulta) => data?.resumen[k] || 0;
  const conMulta = CON_MULTA.reduce((s, k) => s + n(k), 0);
  const sinMulta = SIN_MULTA.reduce((s, k) => s + n(k), 0);
  const importePendiente = [...(porMotivo.get('multa_pendiente') || []), ...(porMotivo.get('multa_transferencia_tarde') || [])]
    .reduce((s, a) => s + (a.multa?.amount || 0), 0);
  const nPendientes = n('multa_pendiente') + n('multa_transferencia_tarde');

  /** Las excepciones se leen JUSTO antes de escribir: otra pestaña pudo cambiarlas. */
  async function escribirExcepciones(cambiar: (e: { prorrogas: Record<string, string>; exentas: any[] }) => void, ok: string) {
    setGuardando(true);
    try {
      const actual = await getSetting('late_fee_exceptions');
      const e = JSON.parse(actual.value || '{}');
      const exc = { prorrogas: e.prorrogas || {}, exentas: Array.isArray(e.exentas) ? e.exentas : [] };
      cambiar(exc);
      await putSetting('late_fee_exceptions', JSON.stringify(exc));
      toast.success(ok);
      cargar();
      return true;
    } catch (err: any) { toast.error(err?.message || 'No se pudo guardar'); return false; }
    finally { setGuardando(false); }
  }

  async function guardarProrroga() {
    if (!fechaProrroga) return;
    if (await escribirExcepciones((e) => { e.prorrogas[mes] = fechaProrroga; }, `Prórroga de ${mesTxt(mes)}: sin pago tardío hasta el ${diaTxt(fechaProrroga)}`)) {
      setEditProrroga(false); setFechaProrroga('');
    }
  }
  const quitarProrroga = () => escribirExcepciones((e) => { delete e.prorrogas[mes]; }, `Prórroga de ${mesTxt(mes)} quitada`);
  const quitarExencion = (a: AlumnaMulta) => escribirExcepciones(
    (e) => { e.exentas = e.exentas.filter((x: any) => !(Number(x.contact_id) === a.contact_id && x.month === mes)); },
    `${a.nombre} vuelve a la regla normal en ${mesTxt(mes)}`);

  async function confirmarAccion(motivo: string) {
    if (!accion) return;
    const a = accion.a;
    if (accion.tipo === 'perdonar' && a.multa) {
      setGuardando(true);
      try { await cancelDebt(a.multa.debt_id, motivo); toast.success(`Pago tardío de ${a.nombre} perdonado`); setAccion(null); cargar(); }
      catch (err: any) { toast.error(err?.message || 'No se pudo perdonar'); }
      finally { setGuardando(false); }
      return;
    }
    // Exentar: la anota en el mes y, si ya tenía la multa sin cobrar, la perdona con el mismo motivo.
    const ok = await escribirExcepciones((e) => {
      e.exentas = e.exentas.filter((x: any) => !(Number(x.contact_id) === a.contact_id && x.month === mes));
      e.exentas.push({ contact_id: a.contact_id, month: mes, nombre: a.nombre, motivo });
    }, `${a.nombre} no llevará pago tardío en ${mesTxt(mes)}`);
    if (ok && a.motivo === 'multa_pendiente' && a.multa) {
      try { await cancelDebt(a.multa.debt_id, `Exenta este mes: ${motivo}`); cargar(); }
      catch { toast.error('Quedó exenta, pero el pago tardío que ya tenía no se pudo perdonar: hazlo desde aquí con «Perdonar»'); }
    }
    if (ok) setAccion(null);
  }

  /** Transferencia tardía → «Mantener»: la multa se queda y sale de la lista de «¿perdonar?». */
  async function mantenerMulta(a: AlumnaMulta) {
    if (!a.multa) return;
    setGuardando(true);
    try {
      const actual = await getSetting('late_fee_wire_kept');
      const ids: number[] = JSON.parse(actual.value || '[]');
      await putSetting('late_fee_wire_kept', JSON.stringify([...new Set([...ids, a.multa.debt_id])]));
      toast.success(`El pago tardío de ${a.nombre} se mantiene`);
      cargar();
    } catch (err: any) { toast.error(err?.message || 'No se pudo guardar'); }
    finally { setGuardando(false); }
  }

  const toggle = (k: string) => setAbiertos((s) => { const x = new Set(s); x.has(k) ? x.delete(k) : x.add(k); return x; });
  const r = data?.reglas;

  return (
    <div className="space-y-4">
      {/* Mes */}
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setMes(mesMas(mes, -1))} className="rounded-lg border border-line bg-surface p-1.5 text-content-2 hover:text-content" aria-label="Mes anterior"><ChevronLeft size={16} /></button>
        <input type="month" value={mes} onChange={(e) => e.target.value && setMes(e.target.value)}
          className="rounded-lg border border-line bg-surface px-2 py-1.5 text-sm text-content outline-none focus:border-brand-blue" />
        <button onClick={() => setMes(mesMas(mes, 1))} disabled={mes >= mesMas(mesDeHoy(), 1)} className="rounded-lg border border-line bg-surface p-1.5 text-content-2 hover:text-content disabled:opacity-40" aria-label="Mes siguiente"><ChevronRight size={16} /></button>
        <h3 className="ml-1 font-display text-lg font-bold capitalize text-content">Pagos tardíos de {mesTxt(mes)}</h3>
      </div>

      {loading || !data ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full rounded-2xl" />)}</div>
      ) : (
        <>
          {/* Reglas con que se juzga este mes */}
          <section className="rounded-2xl border border-line bg-surface p-4 text-sm text-content-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <p className="min-w-0">
                <b className="text-content">Reglas de este mes:</b>{' '}
                vence el {r!.due_day}{r!.primera_clase ? ' (o en su primera clase, si es más tarde)' : ''}
                {' · '}pago tardío de {money(r!.fee)}
                {' · '}{r!.tolerance_days ? `se espera ${r!.tolerance_days} día(s) antes de ponerlo` : 'sin días de tolerancia'}
                {' · '}transferencia {r!.skip_wire ? `sin pago tardío si se registra hasta ${r!.wire_window_days ?? 3} día(s) después de vencer` : 'sí lleva pago tardío'}
                {' · '}cobro desfasado {r!.skip_cycle ? 'sin pago tardío' : 'sí lleva pago tardío'}
              </p>
              {isAdmin && (
                <Link to="/mi-estudio" className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-brand-blue hover:underline">
                  <Settings2 size={13} /> Cambiar reglas
                </Link>
              )}
            </div>
            {/* Prórroga: decisión de ESTE mes */}
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
              <CalendarClock size={15} className="text-content-3" />
              {data.prorroga ? (
                <>
                  <span><b className="text-content">Prórroga:</b> nadie lleva pago tardío de {MESES[Number(mes.slice(5, 7)) - 1]} antes del {diaTxt(data.prorroga)}.</span>
                  {isAdmin && <button onClick={quitarProrroga} disabled={guardando} className="text-xs font-medium text-content-3 underline hover:text-content">Quitar</button>}
                </>
              ) : editProrroga ? (
                <>
                  <span>Sin pago tardío hasta el</span>
                  <input type="date" value={fechaProrroga} min={`${mes}-01`} max={`${mesMas(mes, 1)}-28`} onChange={(e) => setFechaProrroga(e.target.value)}
                    className="rounded-lg border border-line bg-surface px-2 py-1 text-sm text-content" />
                  <button onClick={guardarProrroga} disabled={guardando || !fechaProrroga} className="rounded-lg bg-accent-solid px-3 py-1 text-xs font-semibold text-on-accent disabled:bg-line disabled:text-content-3">Guardar</button>
                  <button onClick={() => setEditProrroga(false)} className="text-xs text-content-3 hover:text-content">Cancelar</button>
                </>
              ) : (
                <>
                  <span className="text-content-3">Sin prórroga este mes.</span>
                  {isAdmin && <button onClick={() => setEditProrroga(true)} className="text-xs font-medium text-brand-blue hover:underline">Dar prórroga (ej. regreso de vacaciones)</button>}
                </>
              )}
            </div>
          </section>

          {data.alumnas.length === 0 ? (
            <EmptyState icon={Gavel} title={`Aún no hay mensualidades de ${mesTxt(mes)}`}
              subtitle="Las mensualidades se generan al empezar el mes. Puedes dejar lista la prórroga desde ahora." />
          ) : (
            <>
              {/* Totales */}
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <Kpi icon={AlertTriangle} tono="rojo" titulo="Con pago tardío" valor={conMulta}
                  sub={nPendientes ? `${nPendientes} sin cobrar · ${money(importePendiente)}` : 'ninguno pendiente'} />
                <Kpi icon={Clock} tono="ambar" titulo="Le toca pago tardío" valor={n('por_multar')}
                  sub={data.es_mes_actual ? 'el proceso de las 7:05 se lo pone' : '—'} />
                <Kpi icon={CheckCircle2} tono="verde" titulo="Sin pago tardío" valor={sinMulta} sub="abre cada motivo para ver quiénes" />
              </div>

              {/* Grupos */}
              <div className="overflow-hidden rounded-2xl border border-line bg-surface">
                {(['por_multar', ...CON_MULTA] as MotivoMulta[]).filter((k) => n(k)).map((k) => (
                  <Grupo key={k} k={k} titulo={data.motivos[k]} filas={porMotivo.get(k) || []} abierto={abiertos.has(k)} onToggle={() => toggle(k)}
                    tono={k === 'por_multar' || k === 'multa_transferencia_tarde' ? 'ambar' : k === 'multa_pendiente' ? 'rojo' : 'neutro'}
                    isAdmin={isAdmin} onAccion={setAccion} onQuitarExencion={quitarExencion} onMantener={mantenerMulta} guardando={guardando} />
                ))}
                {sinMulta > 0 && (
                  <div className="border-t border-line bg-surface-2/50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-content-3">Sin pago tardío, por motivo</div>
                )}
                {SIN_MULTA.filter((k) => n(k)).map((k) => (
                  <Grupo key={k} k={k} titulo={data.motivos[k]} filas={porMotivo.get(k) || []} abierto={abiertos.has(k)} onToggle={() => toggle(k)}
                    tono={REVISAR.includes(k) ? 'ambar' : 'neutro'} isAdmin={isAdmin} onAccion={setAccion} onQuitarExencion={quitarExencion} />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {accion && <MotivoModal accion={accion} mes={mes} guardando={guardando} onClose={() => setAccion(null)} onConfirm={confirmarAccion} />}
    </div>
  );
}

function Kpi({ icon: Icon, tono, titulo, valor, sub }: { icon: typeof Clock; tono: 'rojo' | 'ambar' | 'verde'; titulo: string; valor: number; sub: string }) {
  const c = tono === 'rojo' ? 'text-rose-600 dark:text-rose-400' : tono === 'ambar' ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400';
  return (
    <div className="rounded-2xl border border-line bg-surface p-4">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-content-3"><Icon size={14} className={c} /> {titulo}</p>
      <p className={`mt-1 font-display text-2xl font-bold ${c}`}>{valor}</p>
      <p className="text-xs text-content-3">{sub}</p>
    </div>
  );
}

function Grupo({ k, titulo, filas, abierto, onToggle, tono, isAdmin, onAccion, onQuitarExencion, onMantener, guardando }: {
  k: MotivoMulta; titulo: string; filas: AlumnaMulta[]; abierto: boolean; onToggle: () => void; tono: 'rojo' | 'ambar' | 'neutro';
  isAdmin: boolean; onAccion: (a: Accion) => void; onQuitarExencion: (a: AlumnaMulta) => void;
  onMantener?: (a: AlumnaMulta) => void; guardando?: boolean;
}) {
  const punto = tono === 'rojo' ? 'bg-rose-500' : tono === 'ambar' ? 'bg-amber-500' : 'bg-content-3/40';
  return (
    <div className="border-t border-line first:border-t-0" data-testid={`grupo-${k}`}>
      <button onClick={onToggle} className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm transition hover:bg-surface-2">
        <span className={`h-2 w-2 shrink-0 rounded-full ${punto}`} />
        <span className="min-w-0 flex-1 font-medium text-content">{titulo}</span>
        <span className="rounded-full bg-surface-2 px-2 py-0.5 text-xs font-semibold text-content-2">{filas.length}</span>
        <ChevronDown size={16} className={`shrink-0 text-content-3 transition ${abierto ? 'rotate-180' : ''}`} />
      </button>
      {abierto && k === 'multa_transferencia_tarde' && (
        <p className="border-t border-line bg-amber-50/60 px-4 py-2 text-xs text-content-2 dark:bg-amber-500/5">
          Pagaron la mensualidad por transferencia, pero se registró después de la ventana. Revisa la fecha del comprobante:
          si la hizo a tiempo, <b>Perdonar</b>; si de verdad pagó tarde, <b>Mantener</b> y se le cobra el pago tardío.
        </p>
      )}
      {abierto && (
        <ul className="divide-y divide-line border-t border-line bg-surface-2/30">
          {filas.map((a) => (
            <li key={`${a.contact_id}-${a.debt_id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-sm">
              <div className="min-w-[11rem] flex-1">
                <Link to={`/alumnos/${a.contact_id}`} className="font-medium text-content hover:underline">{a.nombre}</Link>
                <span className="block text-xs text-content-3">{lineaDe(a)}</span>
              </div>
              {isAdmin && (
                <div className="flex shrink-0 gap-1.5">
                  {['multa_pendiente', 'multa_transferencia_tarde'].includes(a.motivo) && (
                    <button onClick={() => onAccion({ tipo: 'perdonar', a })} className="rounded-lg border border-line bg-surface px-2.5 py-1 text-xs font-medium text-content-2 hover:text-content">Perdonar</button>
                  )}
                  {a.motivo === 'multa_transferencia_tarde' && onMantener && (
                    <button onClick={() => onMantener(a)} disabled={guardando} className="rounded-lg border border-line bg-surface px-2.5 py-1 text-xs font-medium text-content-2 hover:text-content disabled:opacity-50">Mantener</button>
                  )}
                  {['por_multar', 'multa_pendiente', 'no_vence'].includes(a.motivo) && (
                    <button onClick={() => onAccion({ tipo: 'exentar', a })} className="rounded-lg border border-line bg-surface px-2.5 py-1 text-xs font-medium text-content-2 hover:text-content">Exentar este mes</button>
                  )}
                  {a.motivo === 'exenta' && (
                    <button onClick={() => onQuitarExencion(a)} className="inline-flex items-center gap-1 rounded-lg border border-line bg-surface px-2.5 py-1 text-xs font-medium text-content-2 hover:text-content"><X size={12} /> Quitar exención</button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Una línea que explica la fila sin abrir nada: plan, cuándo pagó y cómo, y el motivo concreto. */
function lineaDe(a: AlumnaMulta) {
  const partes: string[] = [];
  if (a.plan) partes.push(a.plan);
  if (a.fecha_pago) partes.push(`pagó el ${diaTxt(a.fecha_pago)}${a.metodos.length ? ` (${a.metodos.map((m) => METODO[m] || m).join(', ')})` : ''}`);
  else if (a.debe > 0) partes.push(`debe ${money(a.debe)}`);
  if (a.multa && a.motivo !== 'multa_perdonada') partes.push(`pago tardío ${money(a.multa.amount)} del ${diaTxt(a.multa.creada)}`);
  if (a.detalle && !['tarde_sin_multa'].includes(a.motivo)) partes.push(a.detalle);
  if (a.motivo === 'tarde_sin_multa' && a.detalle) partes.push(a.detalle.replace(/^pagó el \S+, /, ''));
  if (a.motivo === 'por_multar' && a.due_date) partes.push(`venció el ${diaTxt(a.due_date)}`);
  return partes.join(' · ');
}

function MotivoModal({ accion, mes, guardando, onClose, onConfirm }: {
  accion: NonNullable<Accion>; mes: string; guardando: boolean; onClose: () => void; onConfirm: (m: string) => void;
}) {
  const [motivo, setMotivo] = useState('');
  const { tipo, a } = accion;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative w-full max-w-md rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">{tipo === 'perdonar' ? 'Perdonar pago tardío' : 'Exentar este mes'}</h3>
          <button onClick={onClose} className="text-content-3 hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>
        <p className="mb-3 text-sm text-content-2">
          {tipo === 'perdonar'
            ? <><b className="text-content">{a.nombre}</b> · el pago tardío de {money(a.multa?.amount || 0)} queda cancelado con este motivo, a la vista en su ficha.</>
            : <><b className="text-content">{a.nombre}</b> no llevará pago tardío en {mesTxt(mes)}. La mensualidad se le sigue cobrando.{a.motivo === 'multa_pendiente' ? ' El pago tardío que ya tiene se perdona con el mismo motivo.' : ''}</>}
        </p>
        <div className="mb-2 flex flex-wrap gap-1.5">
          {MOTIVOS_RAPIDOS.map((m) => (
            <button key={m} type="button" onClick={() => setMotivo(m)}
              className={`rounded-full border px-2.5 py-1 text-xs transition ${motivo === m ? 'border-transparent bg-accent-solid text-on-accent' : 'border-line bg-surface text-content-2 hover:text-content'}`}>{m}</button>
          ))}
        </div>
        <input value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={200} placeholder="Motivo (obligatorio)" autoFocus
          className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-blue" />
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg px-3 py-2 text-sm text-content-2 hover:text-content">Cancelar</button>
          <button onClick={() => onConfirm(motivo.trim())} disabled={guardando || !motivo.trim()} data-testid="confirmar-motivo"
            className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent disabled:bg-line disabled:text-content-3">
            {guardando ? 'Guardando…' : tipo === 'perdonar' ? 'Perdonar' : 'Exentar'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
